/*
 * `crypto.randomUUID` is only exposed in a **secure context** — HTTPS, or a `localhost` origin.
 * This portal is served over plain HTTP on a Tailscale port-forward
 * (http://macstudio-von-florian.tail3043f4.ts.net:8087), which is neither, so the browser withholds
 * it and Backstage crashes on boot with:
 *
 *     TypeError: globalThis.crypto.randomUUID is not a function
 *         at d.subscribe (module-backstage…js)
 *
 * There are six call sites across the Backstage bundle (the signals plugin is merely the first to
 * run), so removing one plugin would not be a reliable fix.
 *
 * `crypto.getRandomValues` is NOT secure-context-gated and is available here, so this polyfill is
 * cryptographically equivalent to the real thing — it is the same CSPRNG, formatted per RFC 4122
 * v4. It weakens nothing; it only restores an API the browser withholds for transport reasons.
 *
 * This is a workaround for a transport limitation, not the fix. The proper fix is to serve the
 * portal over real TLS, which makes it a secure context and makes this file dead code:
 * enable HTTPS Certificates for the tailnet (Tailscale admin console -> DNS) and front the service
 * with `tailscale serve`. Delete this file when that happens.
 *
 * Imported first in index.tsx so it runs before any Backstage module touches crypto.
 */

function installRandomUuidPolyfill(): void {
  const c: Crypto | undefined = globalThis.crypto;

  // Nothing to do in a secure context, or where the browser gave us the real implementation.
  if (!c || typeof c.randomUUID === 'function') {
    return;
  }

  // Without getRandomValues there is no safe fallback. Fail loudly rather than silently handing
  // out Math.random()-based identifiers.
  if (typeof c.getRandomValues !== 'function') {
    throw new Error(
      'Neither crypto.randomUUID nor crypto.getRandomValues is available. ' +
        'Serve this app over HTTPS or from localhost.',
    );
  }

  const hex: string[] = [];
  for (let i = 0; i < 256; i++) {
    hex.push((i + 0x100).toString(16).slice(1));
  }

  Object.defineProperty(c, 'randomUUID', {
    configurable: true,
    writable: true,
    value: function randomUUID(): `${string}-${string}-${string}-${string}-${string}` {
      const b = new Uint8Array(16);
      c.getRandomValues(b);

      // RFC 4122 §4.4: set the version (4) and variant (10xx) bits.
      b[6] = (b[6] & 0x0f) | 0x40;
      b[8] = (b[8] & 0x3f) | 0x80;

      return (
        hex[b[0]] + hex[b[1]] + hex[b[2]] + hex[b[3]] +
        '-' + hex[b[4]] + hex[b[5]] +
        '-' + hex[b[6]] + hex[b[7]] +
        '-' + hex[b[8]] + hex[b[9]] +
        '-' + hex[b[10]] + hex[b[11]] + hex[b[12]] + hex[b[13]] + hex[b[14]] + hex[b[15]]
      ) as `${string}-${string}-${string}-${string}-${string}`;
    },
  });
}

installRandomUuidPolyfill();
